import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { materialId, smallPerBigOf, supplierId, warehouseRmId } from './wms-master.seed'
import {
  RTN_ACTION_STATUS,
  type RtnActionName,
  type RtnActorCode,
  type RtnStatusCode,
  rtnActionId,
  rtnActorId,
  rtnStatusId,
  WF_RTN_01_ID
} from './workflow-rtn.seed'

/**
 * Return Order RM (C05) — ERD v010: ng_ticket, return_order, return_order_line, rm_ng_pickup_task.
 * Sumber angka: seed prototype `jal-v7.44` (`rm.ng`, `ro.orders`). Dua tiket historis di Karantina NG
 * (NGT-260816-090, NGT-260815-091) sudah ter-attach ke dua Return Order supaya RTN-01/02 langsung bisa
 * diperagakan; tiket NGT-260818-001 (Open) lahir dari QC OPP-125 di `wms-inbound.seed.ts`.
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()

const audit = (userId: string, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})

/** Zona Karantina NG (`QN-NG`) dibuat di `wms-inbound.seed.ts`; Area Staging Retur (`ZS-RM`) dibuat di sini. */
const NG_ZONE_ID = seedId(SEED_GROUP.warehouseZone, 2)
export const RETURN_STAGING_ZONE_ID = seedId(SEED_GROUP.warehouseZone, 4)

type RoSeed = {
  no: string
  supplier: 'SUP-BMJ' | 'SUP-IJ'
  status: RtnStatusCode
  createdAt: string
  ticketNo: string
  planDate?: string
  planNote?: string
  purchasingBy?: string
  steps: [RtnActionName, RtnActorCode, string, string][]
}

const RETURN_ORDERS: RoSeed[] = [
  {
    no: 'RO-260817-001',
    supplier: 'SUP-BMJ',
    status: 'RTN_PENDING_PURCH',
    createdAt: '2026-08-17 09:20',
    ticketNo: 'NGT-260815-091',
    steps: [
      ['ACT_RTN_SUBMIT', 'ACT_RTN_ADMIN_RM', 'yulia', '2026-08-17 10:05'],
      ['ACT_RTN_APP_SPV', 'ACT_RTN_SPV_RM', 'galih', '2026-08-17 13:40'],
      ['ACT_RTN_APP_KADIV', 'ACT_RTN_KADIV_RM', 'alex', '2026-08-18 08:15']
    ]
  },
  {
    no: 'RO-260818-002',
    supplier: 'SUP-IJ',
    status: 'RTN_APPROVED',
    createdAt: '2026-08-18 08:40',
    ticketNo: 'NGT-260816-090',
    planDate: '2026-08-19',
    planNote: 'Truk supplier ambil sendiri, pagi',
    purchasingBy: 'rudi',
    steps: [
      ['ACT_RTN_SUBMIT', 'ACT_RTN_ADMIN_RM', 'yulia', '2026-08-18 09:05'],
      ['ACT_RTN_APP_SPV', 'ACT_RTN_SPV_RM', 'galih', '2026-08-18 09:50'],
      ['ACT_RTN_APP_KADIV', 'ACT_RTN_KADIV_RM', 'alex', '2026-08-18 10:30'],
      ['ACT_RTN_APP_PURCH', 'ACT_RTN_PURCHASING', 'rudi', '2026-08-18 11:20']
    ]
  }
]

/** [tiket, QR unit NG, material, batch, qty, unit besar, supplier, parameter, hasil, dibuat, oleh]. */
const HISTORIC_NG_TICKETS = [
  [
    'NGT-260816-090',
    'ETK-XTRA20:20260812:01:0002',
    'ETK-XTRA20',
    'ETK-XTRA20-260812-01',
    2,
    'Box',
    'SUP-IJ',
    'Cut to Cut',
    'Defect cutting tidak sesuai creasing',
    '2026-08-16 10:00',
    'karno'
  ],
  [
    'NGT-260815-091',
    'FOIL-BLK86:20260815:01:0001',
    'FOIL-BLK86',
    'FOIL-BLK86-260815-01',
    1,
    'Roll',
    'SUP-BMJ',
    'Ukuran',
    'Lebar foil di bawah toleransi',
    '2026-08-15 14:20',
    'tono'
  ]
] as const

export function buildWmsReturnSeed(): SeedTables {
  const admin = USER_IDS.yulia
  const warehouse_zone: LocalRow[] = [
    {
      ...audit(admin, '2026-08-01 07:00'),
      id: RETURN_STAGING_ZONE_ID,
      code: 'ZS-RM',
      name: 'Area Staging Retur',
      warehouse_id: warehouseRmId,
      zone_type: 'STAGING_RETURN'
    }
  ]

  const rm_unit: LocalRow[] = []
  const ng_ticket: LocalRow[] = []
  HISTORIC_NG_TICKETS.forEach(([ticket, qr, material, batch, qty, unit, supplier, param, hasil, at, by], index) => {
    const unitId = seedId(SEED_GROUP.rmUnit, 300 + index + 1)
    rm_unit.push({
      ...audit(USER_IDS[by], at),
      id: unitId,
      uid: `UNIT-${String(300 + index + 1).padStart(6, '0')}`,
      qr_code: qr,
      material_id: materialId(material),
      batch_no: batch,
      supplier_id: supplierId(supplier),
      po_no: null,
      qty_small: qty * smallPerBigOf(material),
      rack_bin_id: null,
      warehouse_zone_id: NG_ZONE_ID,
      flag: 'NG',
      unit_status: 'NG_IN_QUARANTINE',
      qc_date: utc(at),
      unit_big_snapshot: unit,
      small_per_big_snapshot: smallPerBigOf(material),
      is_leftover_wip: false,
      stored_at: utc(at)
    })
    ng_ticket.push({
      ...audit(USER_IDS[by], at),
      id: seedId(SEED_GROUP.ngTicket, 100 + index + 1),
      ticket_no: ticket,
      unit_id: unitId,
      supplier_id: supplierId(supplier),
      qty,
      parameter_result: `${param} — ${hasil}`,
      placement_rack_bin_id: null,
      placement_warehouse_zone_id: NG_ZONE_ID,
      ticket_status: 'ATTACHED'
    })
  })

  const return_order: LocalRow[] = []
  const return_order_line: LocalRow[] = []
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  RETURN_ORDERS.forEach((ro, index) => {
    const roId = seedId(SEED_GROUP.returnOrder, index + 1)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 400 + index + 1)
    const ticket = ng_ticket.find(row => row.ticket_no === ro.ticketNo) as LocalRow
    const last = ro.steps[ro.steps.length - 1]
    return_order.push({
      ...audit(admin, ro.createdAt),
      id: roId,
      ro_no: ro.no,
      supplier_id: supplierId(ro.supplier),
      status_id: rtnStatusId(ro.status),
      submission_id: submissionId,
      plan_date: ro.planDate ?? null,
      plan_note: ro.planNote ?? null,
      preparer_id: null,
      shipped_at: null,
      carrier: null,
      sjr_no: null,
      is_shipped: false
    })
    return_order_line.push({
      ...audit(admin, ro.createdAt),
      id: seedId(SEED_GROUP.returnOrderLine, index + 1),
      ro_id: roId,
      ng_ticket_id: ticket.id
    })
    wxl_submission.push({
      ...audit(admin, ro.createdAt),
      id: submissionId,
      workflow_id: WF_RTN_01_ID,
      entity: 'return_order',
      entity_id: roId,
      source: 'ACTION',
      current_status_id: rtnStatusId(ro.status),
      last_action_id: rtnActionId(last[0]),
      workflow_snapshot: null
    })
    ro.steps.forEach(([action, actor, by, at], step) => {
      const [from, to] = RTN_ACTION_STATUS[action]
      wxl_submission_log.push({
        ...audit(USER_IDS[by], at),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 4000 + (index + 1) * 10 + step),
        workflow_id: WF_RTN_01_ID,
        submission_id: submissionId,
        action_id: rtnActionId(action),
        actor_id: rtnActorId(actor),
        from_status_id: rtnStatusId(from),
        to_status_id: rtnStatusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason: null,
        entity: 'return_order',
        entity_id: roId
      })
    })
  })

  return {
    warehouse_zone,
    rm_unit,
    ng_ticket,
    return_order,
    return_order_line,
    rm_ng_pickup_task: [],
    wxl_submission,
    wxl_submission_log
  }
}
